import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthShell, { Field, PrimaryBtn } from "./AuthShell";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Search, Building2, Check } from "lucide-react";

export default function RegisterResident() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [condo, setCondo] = useState(null);
  const [tower, setTower] = useState("");
  const [unit, setUnit] = useState("");

  const next = (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return toast.error("As senhas não coincidem.");
    if (f.password.length < 6) return toast.error("A senha deve ter ao menos 6 caracteres.");
    setStep(2);
  };

  const search = async () => {
    setSearching(true);
    try {
      const { data } = await api.get("/condominiums/search", { params: { q: query } });
      setResults(data);
      if (data.length === 0) toast.message("Nenhum condomínio encontrado com esse termo.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSearching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!condo) return toast.error("Selecione seu condomínio.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/resident", {
        name: f.name, email: f.email, phone: f.phone, password: f.password,
        condominium_id: condo.id, tower, unit,
      });
      login(data.token, data.user);
      toast.success("Cadastro enviado! Aguarde a aprovação do síndico.");
      nav("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testid="register-resident-page"
      title={step === 1 ? "Criar sua conta" : "Encontre seu condomínio"}
      subtitle={step === 1 ? "Etapa 1 de 2 — Seus dados" : "Etapa 2 de 2 — Localize e informe sua unidade"}
    >
      {step === 1 ? (
        <form onSubmit={next} className="space-y-4">
          <Field label="Nome completo" data-testid="res-name" value={f.name} onChange={up("name")} required />
          <Field label="E-mail" type="email" data-testid="res-email" value={f.email} onChange={up("email")} required />
          <Field label="Celular" data-testid="res-phone" value={f.phone} onChange={up("phone")} placeholder="(11) 99999-9999" required />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Senha" type="password" data-testid="res-password" value={f.password} onChange={up("password")} required />
            <Field label="Confirmar senha" type="password" data-testid="res-confirm" value={f.confirm} onChange={up("confirm")} required />
          </div>
          <PrimaryBtn type="submit" data-testid="res-next">Continuar</PrimaryBtn>
        </form>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <span className="text-xs font-medium text-muted-foreground">Buscar por nome, endereço ou código</span>
            <div className="mt-1.5 flex gap-2">
              <input
                data-testid="res-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
                placeholder="Ex: D Garden, Tatuapé, DGARDEN"
                className="flex-1 rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-primary/60 transition-colors"
              />
              <button type="button" onClick={search} data-testid="res-search-btn" className="px-4 rounded-xl brand-gradient text-white active:scale-95 transition-transform">
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>

          {searching && <p className="text-sm text-muted-foreground">Buscando...</p>}

          <div className="space-y-2 max-h-52 overflow-y-auto">
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                data-testid="res-condo-option"
                onClick={() => setCondo(c)}
                className={`w-full text-left rounded-2xl p-3 border flex items-center gap-3 transition-colors ${
                  condo?.id === c.id ? "border-primary/60 bg-primary/10" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"
                }`}
              >
                <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.neighborhood}, {c.city}/{c.state} · {c.code}</p>
                </div>
                {condo?.id === c.id && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>

          {condo && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Torre/Bloco" data-testid="res-tower" value={tower} onChange={(e) => setTower(e.target.value)} placeholder="Torre 2" required />
              <Field label="Apartamento/Unidade" data-testid="res-unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="1202" required />
            </div>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={() => setStep(1)} className="flex-1 rounded-full bg-white/5 border border-white/10 py-3 font-medium hover:bg-white/10 transition-colors" data-testid="res-back">Voltar</button>
            <div className="flex-1"><PrimaryBtn type="submit" loading={loading} data-testid="res-submit">Solicitar entrada</PrimaryBtn></div>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
